import {useMemo} from 'react'

import {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import type {SingleSelectValue} from '../../../api/columns/contracts/single-select'
import type {ColumnData} from '../../../api/columns/contracts/storage'
import {useFilteredItems} from '../../../features/filtering/hooks/use-filtered-items'
import {getGroupByFieldId} from '../../../features/grouping/get-group-by-field'
import {getGroupingMetadataFromServerGroupValue} from '../../../features/grouping/get-grouping-metadata'
import {buildGroupingConfiguration} from '../../../features/grouping/grouping-metadata-configurations'
import {useHorizontalGroupedBy} from '../../../features/grouping/hooks/use-horizontal-grouped-by'
import {useVerticalGroups} from '../../../features/grouping/hooks/use-vertical-groups'
import {useSortedItems} from '../../../features/sorting/hooks/use-sorted-items'
import {useEnabledFeatures} from '../../../hooks/use-enabled-features'
import {useViews} from '../../../hooks/use-views'
import type {ColumnModel} from '../../../models/column-model'
import type {Group} from '../../../models/group'
import type {HorizontalGrouping, ItemsGroupedByVerticalColumn} from '../../../models/horizontal-group'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {canVerticalGroup, MissingVerticalGroupId} from '../../../models/vertical-group'
import {useAllColumns} from '../../../state-providers/columns/use-all-columns'
import {
  type GroupQueries,
  usePaginatedMemexItemsQuery,
} from '../../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {Resources} from '../../../strings'
import {buildGetGroupedRowsFunctionForBoard} from '../grouping'

const defaultGroupByFieldOptions: Array<Group> = []
/**
 * Filters and groups items depending on the current view.
 * Steps:
 * 1. Apply the current filter to the full list of items.
 * 2. If the current view is not grouped, we will fall back to the `Status` field.
 * 3. If the current view is grouped, we will create a list of groups, each with a list of items, based on the vertical grouping field.
 * @returns An array of items grouped by the vertical group they belong to
 */
export function useBoardItems() {
  const {memex_table_without_limits} = useEnabledFeatures()
  const {filteredItems} = useFilteredItems()
  const {groupedByColumnId, groupByFieldOptions = defaultGroupByFieldOptions} = useVerticalGroups()
  const {groupedByColumn: horizontalGroupedByColumn, collapsedGroups} = useHorizontalGroupedBy()
  const {sortUngroupedItems} = useSortedItems()
  const {missingRequiredColumnData} = useViews()
  const {allColumns} = useAllColumns()
  const compatibleColumns = useMemo(() => allColumns.filter(canVerticalGroup), [allColumns])

  const groupByFieldId = useMemo(
    () => getGroupByFieldId(compatibleColumns, groupedByColumnId),
    [compatibleColumns, groupedByColumnId],
  )

  const groupByField = useMemo(() => allColumns.find(c => c.id === groupByFieldId), [allColumns, groupByFieldId])

  const groupingConfig = useColumnsWithGroupingConfiguration()

  // `memex_table_without_limits` will not change after initial render
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const paginatedMemexItems = memex_table_without_limits ? usePaginatedMemexItemsQuery() : null
  const groupedItems: HorizontalGrouping = useMemo(() => {
    let allItemsByVerticalGroup: ItemsGroupedByVerticalColumn
    if (memex_table_without_limits && paginatedMemexItems) {
      allItemsByVerticalGroup = Object.fromEntries(
        Object.entries(paginatedMemexItems.itemQueriesByGroup).map(([_, group]) => {
          return formatGroupFromServer(group, groupByFieldId, groupByField)
        }),
      )
    } else {
      allItemsByVerticalGroup = Object.fromEntries(
        Object.entries(
          groupItemsByField(
            filteredItems,
            groupByFieldId,
            groupByFieldOptions,
            missingRequiredColumnData,
            MissingVerticalGroupId,
          ),
        ).map(([fieldValue, items]) => {
          // defining totalCount to align with MWL data shape
          return [fieldValue, {items: sortUngroupedItems(items), totalCount: items.length}] as const
        }),
      )
    }

    if (!memex_table_without_limits && horizontalGroupedByColumn) {
      const groupConfig = groupingConfig[horizontalGroupedByColumn?.id]

      const horizontalGroups =
        groupConfig
          ?.groupingConfig?.(filteredItems)
          .map(horizontalGroup => {
            const itemsByVerticalGroup = Object.fromEntries(
              Object.entries(
                groupItemsByField(
                  horizontalGroup.rows,
                  groupByFieldId,
                  groupByFieldOptions,
                  missingRequiredColumnData,
                  MissingVerticalGroupId,
                ),
              ).map(([fieldValue, items]) => {
                return [fieldValue, {items: sortUngroupedItems(items)}] as const
              }),
            )
            return {
              ...horizontalGroup,
              isCollapsed: collapsedGroups.includes(horizontalGroup.value),
              itemsByVerticalGroup,
            }
          })
          .filter(horizontalGroup => horizontalGroup.rows.length > 0) ?? []

      if (horizontalGroups.length > 0) {
        return {
          isHorizontalGrouped: true as const,
          horizontalGroupedByColumn,
          allItemsByVerticalGroup,
          horizontalGroups,
        }
      }
    }

    return {
      isHorizontalGrouped: false as const,
      allItemsByVerticalGroup,
      horizontalGroups: [
        {
          value: Resources.undefined,
          isCollapsed: false,
          itemsByVerticalGroup: allItemsByVerticalGroup,
          rows: filteredItems,
        },
      ],
    }
  }, [
    paginatedMemexItems,
    horizontalGroupedByColumn,
    filteredItems,
    groupByField,
    groupByFieldOptions,
    groupByFieldId,
    missingRequiredColumnData,
    sortUngroupedItems,
    groupingConfig,
    collapsedGroups,
    memex_table_without_limits,
  ])

  return useMemo(() => {
    return {
      groupByFieldOptions,
      groupByFieldId,
      groupByField,
      compatibleColumns,
      filteredItems,
      groupedItems,
    }
  }, [groupByFieldOptions, groupByFieldId, groupByField, compatibleColumns, filteredItems, groupedItems])
}

/**
 * Utility function that prepares items grouped server-side for use by the Board component.
 * Metadata for the group is fetched from the server as well and used to get its client-side id.
 * "Empty" groups will be given a `MissingVerticalGroupId`.
 * Currently only supports single-select and iteration fields.
 *
 * @param group Object containing server-side grouped items and group metadata
 * @param groupByField Column object for field to group by
 * @returns An array containing the client-side id of the group and an object with the items in the group
 */
function formatGroupFromServer<TColumnId extends keyof ColumnData>(
  group: GroupQueries,
  groupByFieldId: TColumnId,
  groupByField?: ColumnModel,
): [string, {items: ReadonlyArray<MemexItemModel>; groupedColumnId?: string; totalCount?: number}] {
  if (!groupByField) {
    throw new Error(`Column with id ${groupByFieldId} is used for grouping but does not have a valid model`)
  }

  let id = MissingVerticalGroupId
  const serverMetadata = getGroupingMetadataFromServerGroupValue(groupByField, group.groupValue)
  if (serverMetadata && serverMetadata.kind !== 'empty') {
    switch (serverMetadata.dataType) {
      case MemexColumnDataType.Iteration:
        id = serverMetadata.value.iteration.id
        break
      case MemexColumnDataType.SingleSelect:
        id = serverMetadata.value.option.id
        break
      default:
        throw new Error(`Board view does not support column by ${serverMetadata.dataType} column type`)
    }
  }

  return [
    id,
    {
      items: group.queries.flatMap(q => q.data?.nodes ?? []),
      totalCount: group.totalCount?.value,
      groupedColumnId: group.groupId,
    },
  ]
}

/**
 * Utility function that actually performs the grouping of items, based on their values for the
 * group by field.
 * Items without a value for the group by field will be grouped together in a group with the id of the
 * `missingGroupId` parameter.
 * Currently only supports single-select and iteration fields.
 *
 * @param items Flat, ungrouped list of memex items this method will group
 * @param groupByFieldId The id of the field to group by
 * @param fieldOptions An array of valid groups associated with the field to group by
 * @param missingRequiredColumnData Whether or not we are currently missing column values for the field to group by
 * @param missingGroupId The id of a hard-coded group to use for items that do not have any group by value
 * @returns A mapping of group ids to arrays of items belonging to that group
 */
function groupItemsByField<TColumnId extends keyof ColumnData>(
  items: Readonly<Array<MemexItemModel>>,
  groupByFieldId: TColumnId,
  fieldOptions: Array<Group>,
  missingRequiredColumnData: boolean,
  missingGroupId: string,
) {
  const groupedBy: {[fieldValue: string]: Array<MemexItemModel>} = Object.fromEntries([
    [missingGroupId, []],
    ...fieldOptions.map<[string, []]>(option => [option.id, []]),
  ])

  if (missingRequiredColumnData) {
    return groupedBy
  }
  for (const item of items) {
    const fieldValue = getGroupByFieldValueForItem(item.columns, groupByFieldId, missingGroupId)

    if (fieldValue in groupedBy || fieldValue === missingGroupId) {
      groupedBy[fieldValue]?.push(item)
    }
  }
  return groupedBy
}

function getGroupByFieldValueForItem<TColumnId extends keyof ColumnData>(
  columnData: ColumnData,
  groupByFieldId: TColumnId,
  missingGroupId: string,
): string {
  if (groupByFieldId === -1) {
    return missingGroupId
  }
  const singleSelectValue = columnData[groupByFieldId] as SingleSelectValue
  if (!singleSelectValue || !singleSelectValue.id) {
    return missingGroupId
  }

  return singleSelectValue.id
}

const boardGroupingConfig = buildGroupingConfiguration<Readonly<MemexItemModel>>(row => row.columns)
function getColumnBehaviors(columnModel: Pick<ColumnModel, 'dataType'>) {
  return boardGroupingConfig[columnModel.dataType]
}
const useColumnsWithGroupingConfiguration = () => {
  const {allColumns} = useAllColumns()
  const groupConfigurations = useMemo(() => {
    return Object.fromEntries(
      allColumns.map(column => {
        const behaviors = getColumnBehaviors(column)
        return [
          column.id,
          {
            ...column,
            groupingConfig: behaviors ? buildGetGroupedRowsFunctionForBoard(column, behaviors) : undefined,
          },
        ] as const
      }),
    )
  }, [allColumns])

  return groupConfigurations
}
